import { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosInstanceAdobeSign from './axios';
import { WrapImsPacket } from '../../store/utility';
import { deepCloneObject } from '../../store/utility';

//#region Utilities
/**
 * Asynchronously paginates @param request and returns retrieved @param keyPayload data.
 * @param {Function} request HTTP request method to be paginated.
 * @param {string} keyPayload unique identifier of property of interest in response payload to retrieve.
 * @param {object=} requestPayload nullable payload for paginated HTTP request method.
 * @param {AxiosRequestConfig=} config nullable HTTP request configurations.
 * @param {boolean=} [isSearch=false] toggle distinct pagination mechanism for search requests.
 * @returns {Promise<any[]>} array of paginated data.
 */
export const getPaginated = async (
	request,
	keyPayload,
	requestPayload = null,
	config = null,
	isSearch = false
) => {
	return new Promise(async (resolve, reject) => {
		let arrData = [];
		let payload = deepCloneObject(requestPayload);
		let indexPagination = isSearch ? 0 : '';
		let response;

		try {
			do {
				// preserve optional custom configuration while guaranteeing pagination configuration
				config = {
					...config,
					params:
						config && config.params
							? {
									...config.params,
									...(!isSearch && {
										cursor: indexPagination,
									}),
							  }
							: !isSearch
							? { cursor: indexPagination }
							: null,
				};
				if(payload){
					response = await request(payload, config);
				}
				else{
					response = await request(config);
				}
				if (
					response?.data?.payload
				) {
					// support multi-level property accessing of response payload
					let data = keyPayload
						?.split('.')
						.reduce((a, b) => a[b], response.data.payload);
					arrData = [
						...arrData,
						...data,
					];
					indexPagination = isSearch
						? response.data.payload.agreementAssetsResults
								?.searchPageInfo?.nextIndex
						: response.data.payload['page']['nextCursor'];
					if(isSearch){
						payload.agreementAssetsCriteria.startIndex =
							indexPagination;
					}
				}
			} while (indexPagination);
			resolve(arrData);
		} catch (ex) {
			reject(ex);
		}
	});
};
//#endregion

//#region Agreements
/**
 * Asynchronously retrieves Adobe Sign agreements.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object[]>>} axios response containing agreement object array. Response paginated depending on number of returned agreements.
 */
export const getAgreements = async (config = null) => {
	return await axiosInstanceAdobeSign.get('agreements', config);
};

/**
 * Asynchronously retrieves Adobe Sign agreement.
 * @param {string} id unique identifier of agreement of interest.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing agreement object.
 */
export const getAgreement = async (id, config = null) => {
	return await axiosInstanceAdobeSign.get(`agreements/${id}`, config);
};

/**
 * Asynchronously retrieves participant set of Adobe Sign agreement.
 * @param {string} id unique identifier of agreement of interest.
 * @param {string} participantSetId unique identifier of participant set of interest.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing participant set object.
 */
export const getParticipantSet = async (
	id,
	participantSetId,
	config = null
) => {
	return await axiosInstanceAdobeSign.get(
		`agreements/${id}/members/participantSets/${participantSetId}`,
		config
	);
};

/**
 * Asynchronously creates a new Adobe Sign agreement.
 * @param {import('./_payloads').AgreementInfo} agreementInfo new agreement configuration.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing library document object(s). Response paginated depending on number of returned documents.
 */
export const postAgreement = async (agreementInfo, config = null) => {
	return await axiosInstanceAdobeSign.post(
		'agreements',
		WrapImsPacket(agreementInfo),
		config
	);
};

/**
 * Asynchronously initiates reminder communications for agreement.
 * @param {string} id unique identifier of agreement of interest.
 * @param {import('./_payloads').ReminderInfo} reminderInfo reminder configuration.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<string>>} axios response containing unique identifier of created reminder.
 */
export const postReminder = async (id, reminderInfo, config = null) => {
	return await axiosInstanceAdobeSign.post(
		`agreements/${id}/reminders`,
		WrapImsPacket(reminderInfo),
		config
	);
};

/**
 * Asynchronously updates participant set @param participantSetId of agreement @param agreementId.
 * @param {string} agreementId unique identifier of agreement of interest.
 * @param {string} participantSetId unique identifier of participant set of interest.
 * @param {import('./_payloads').DetailedParticipantSetInfo} participantSetInfo participant set configuration.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse>} empty axios response.
 */
export const putParticipantSet = async (
	agreementId,
	participantSetId,
	participantSetInfo,
	config = null
) => {
	return await axiosInstanceAdobeSign.put(
		`agreements/${agreementId}/members/participantSets/${participantSetId}`,
		WrapImsPacket(participantSetInfo),
		config
	);
};

/**
 * Asynchronously updates state of agreement @param agreementId.
 * @param {string} id unique identifier of agreement of interest.
 * @param {import('./_payloads').AgreementStateInfo} stateInfo state configuration.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse>} empty axios response.
 */
export const putState = async (id, stateInfo, config = null) => {
	return await axiosInstanceAdobeSign.put(
		`agreements/${id}/state`,
		WrapImsPacket(stateInfo),
		config
	);
};
//#endregion

//#region Groups
/**
 * Asynchronously retrieves Adobe Sign user collections.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing user collection object(s). Response paginated depending on number of returned collections.
 */
export const getGroups = async (config = null) => {
	return await axiosInstanceAdobeSign.get('groups', config);
};
//#endregion

//#region Library Documents
/**
 * Asynchronously retrieves Adobe Sign library documents.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing library document object(s). Response paginated depending on number of returned documents.
 */
export const getLibraryDocuments = async (config = null) => {
	return await axiosInstanceAdobeSign.get('libraryDocuments', config);
};
//#endregion

//#region Search
/**
 * Asynchronously retrieves Adobe Sign agreement assets satisfying search criteria.
 * @param {import('./_payloads').SearchBody} searchBody search criteria.
 * @param {AxiosRequestConfig} config nullable HTTP request configurations.
 * @returns {Promise<AxiosResponse<object>>} axios response containing agreement object(s). Response paginated depending on number of returned agreements.
 */
export const postSearch = async (searchBody, config = null) => {
	return await axiosInstanceAdobeSign.post(
		'search',
		WrapImsPacket(searchBody),
		config
	);
};
//#endregion
